import { IInvestmentDataModel } from './investment.data-model';
import { IRoundDataModel } from './round.data-model';

export interface ICompanyFundingsDataModel {
  fundingRoundCount: number;
  acceleratorIncubatorAlum: string[];
  totalRaisedToDate: number;
  lastFundingAmount: number;
  lastFundingDate: string;
  lastFundingType: string;
  fundings: IFundingDataModel[]; // array of funding rounds sorted by date, ascending
}

export interface IFundingDataModel {
  dealId: number;
  dealDate: string;
  fundingRoundId: number;
  fundingRound: string;
  fundingRoundCategoryId: number;
  fundingRoundCategory: string;
  dealSizeInMillions: number | null;
  valuationInMillions: number | null;
  investors: Investor[];
  leadInvestors?: string[];
  fundedOrg: Investor;
  valuationSourceType: string;
  valuationIsEstimate: boolean;
  valuationSourceUrls: string[] | null;
  lastUpdateTime: string;
  investmentAmount?: number; // Investment made in this specific round
  cumulativeInvestment?: number; // Running total of all investments up to this round
}

export interface Investor {
  orgId: number;
  orgName: string;
  url: null | string;
}

export function createFundingDataModel(
  overrides: Partial<IFundingDataModel> & { investmentAmount?: number } = {}
): IFundingDataModel {
  return {
    dealId: 0,
    dealDate: '',
    fundingRoundId: 0,
    fundingRound: '',
    fundingRoundCategoryId: 0,
    fundingRoundCategory: '',
    dealSizeInMillions: 0,
    valuationInMillions: null,
    investors: [],
    leadInvestors: [],
    investmentAmount: 0,
    fundedOrg: {
      orgId: 0,
      orgName: '',
      url: null,
    },
    valuationSourceType: '',
    valuationIsEstimate: false,
    valuationSourceUrls: null,
    lastUpdateTime: '',
    ...overrides,
  };
}

/**
 * Wire shape of GET /company/:id/fundings. The API pre-joins lead investor
 * names into a single comma-separated string (CBInsights/Crunchbase market
 * data); names on this path are clean display names, so splitting on commas
 * is unambiguous. Aumni-sourced legal names (which embed commas) never flow
 * through this endpoint — they arrive via the round tracker as string[].
 * investmentAmount / cumulativeInvestment are FE-computed (calculateFundingMetrics),
 * never on the wire.
 */
export type IFundingRawResponse = Omit<
  IFundingDataModel,
  'leadInvestors' | 'investmentAmount' | 'cumulativeInvestment'
> & {
  leadInvestors?: string | string[] | null;
};

export type ICompanyFundingsRawResponse = Omit<ICompanyFundingsDataModel, 'fundings'> & {
  fundings: IFundingRawResponse[] | null;
};

export function mapCompanyFundingsResponse(
  raw: ICompanyFundingsRawResponse | null
): ICompanyFundingsDataModel | null {
  if (!raw) {
    return null;
  }
  return {
    ...raw,
    fundings:
      raw.fundings?.map((funding) => ({
        ...funding,
        leadInvestors: toLeadInvestorsArray(funding.leadInvestors),
      })) ?? [],
  };
}

function toLeadInvestorsArray(raw: string | string[] | null | undefined): string[] {
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  return (
    raw
      ?.split(',')
      .map((name) => name.trim())
      .filter(Boolean) ?? []
  );
}

export function roundTrackerMetricToFundingModel(
  r: IInvestmentDataModel,
  roundsById: Map<number, IRoundDataModel>
): IFundingDataModel {
  const {
    investmentDate,
    raiseAmount,
    postMoneyValuation,
    leadInvestors,
    coInvestors,
    roundId,
    gcAmount,
    position,
  } = r;
  return createFundingDataModel({
    fundingRoundId: roundId ?? 0,
    fundingRound: position ?? '',
    fundingRoundCategory: roundsById.get(roundId ?? -1)?.name ?? '',
    dealDate: investmentDate,
    investmentAmount: gcAmount ?? undefined,
    dealSizeInMillions: typeof raiseAmount === 'number' ? raiseAmount / 1_000_000 : undefined,
    valuationInMillions:
      typeof postMoneyValuation === 'number' ? postMoneyValuation / 1_000_000 : postMoneyValuation,
    leadInvestors: leadInvestors ?? [],
    investors: coInvestors?.map((coInvestor) => ({
      orgId: -1,
      orgName: coInvestor,
      url: null,
    })),
  });
}

export function createCompanyFundingDataModel(
  overrides?: Partial<ICompanyFundingsDataModel>
): ICompanyFundingsDataModel {
  return {
    fundingRoundCount: 0,
    acceleratorIncubatorAlum: [],
    totalRaisedToDate: 0,
    lastFundingAmount: 0,
    lastFundingDate: '',
    lastFundingType: '',
    fundings: [],

    ...overrides,
  };
}
